import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashDebounce from 'lodash/debounce';

import GoogleMap from 'react-google-map';

import withGoogleMaps from 'client/helpers/withGoogleMaps';
import {
  getMapArea,
  centerMarkersOnMap,
} from 'client/helpers/googleMapsHelpers';

@withGoogleMaps
class SrListingsMapViewManager extends Component {
  static propTypes = {
    mapZoomLevel: PropTypes.number.isRequired,
    mapCenter: PropTypes.object,
    googleMaps: PropTypes.object.isRequired,
    listings: PropTypes.array.isRequired,
    viewingListing: PropTypes.object,
    bookmarksByListingId: PropTypes.array.isRequired,
    getGoogleMapsConfig: PropTypes.func.isRequired,
    getMarkerConfig: PropTypes.func.isRequired,
    getMarkerShape: PropTypes.func.isRequired,
    widgets: PropTypes.array,
    onClickListing: PropTypes.func,
    onMapZoomChange: PropTypes.func,
    onClickMap: PropTypes.func,
  }

  constructor(props) {
    super(props);

    this.state = {
      map: null,
      markers: new Map(),
    };
  }

  componentDidUpdate() {
    const { markers } = this.state;
    const { listings } = this.props;

    const listingIds = listings.map((listing) => listing.id);
    const newListings = listings.filter((listing) => !markers.has(listing.id));
    const oldListings = Array.from(markers.keys()).filter((listingId) => !listingIds.includes(listingId));

    if (oldListings.length) {
      this._removeListingsFromMap(oldListings);
    }

    if (newListings.length) {
      this._addListingsToMap(newListings);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      (this.props.viewingListing !== nextProps.viewingListing)
      || (this.props.bookmarksByListingId.length !== nextProps.bookmarksByListingId.length)
    ) {
      const { markers, map } = this.state;
      const {
        viewingListing,
        googleMaps,
        getMarkerConfig,
      } = nextProps;
      if (viewingListing) {
        const marker = markers.get(viewingListing.id);
        if (marker) {
          marker.setOptions(
            getMarkerConfig({
              map,
              googleMaps,
              listing: viewingListing,
            })
          );
        }
      }
      if (this.props.viewingListing) {
        const marker = markers.get(this.props.viewingListing.id);
        if (marker) {
          marker.setOptions(
            getMarkerConfig({
              map,
              googleMaps,
              listing: this.props.viewingListing,
            })
          );
        }
      }
    }
  }

  _onLoadedMap = (googleMaps, map) => {
    const { listings } = this.props;

    this.setState({ map }, () => {
      this._addListingsToMap(listings);

      map.addListener('bounds_changed', this._onBoundsChange);
      map.addListener('zoom_changed', this._onZoomChange);
      map.addListener('center_changed', this._onCenterChange);
      map.addListener('click', this._onClickMap);
    });
  }

  _onBoundsChange = lodashDebounce(() => {
    const { map } = this.state;
    const { onMapAreaChange } = this.props;
    const mapArea = getMapArea({ map });

    if (onMapAreaChange) {
      onMapAreaChange({ mapArea });
    }
  }, 1000);

  _onZoomChange = () => {
    const { map, markers } = this.state;
    const {
      googleMaps,
      mapZoomLevel,
      listings,
      getMarkerConfig,
      getMarkerShape,
      onMapZoomChange,
    } = this.props;

    const newMapZoomLevel = map.getZoom();
    if (onMapZoomChange) {
      onMapZoomChange(newMapZoomLevel);
    }

    listings.forEach((listing) => {
      if (markers.has(listing.id)) {
        const newMarkerShape = getMarkerShape({ mapZoomLevel: newMapZoomLevel, listing });

        if (newMarkerShape !== getMarkerShape({ mapZoomLevel, listing })) {
          const marker = markers.get(listing.id);
          marker.setOptions(
            getMarkerConfig({
              map,
              googleMaps,
              listing,
            })
          );
        }
      }
    });
  };

  _onCenterChange = lodashDebounce(() => {
    const { map } = this.state;
    const { onMapCenterChange } = this.props;
    const mapCenter = map.getCenter();

    if (onMapCenterChange) {
      onMapCenterChange({
        lat: mapCenter.lat(),
        lng: mapCenter.lng(),
      });
    }
  }, 400)

  _onClickMap = () => {
    const { onClickMap } = this.props;
    if (onClickMap) {
      onClickMap();
    }
  }

  _addListingsToMap = (listings) => {
    const { map, markers } = this.state;
    const { googleMaps, centerMarkers } = this.props;

    listings.forEach((listing) => {
      if (!markers.has(listing.id)) {
        markers.set(listing.id, this._addListingToMap(listing));
      }
    });

    this.setState({ markers });

    if (listings.length && centerMarkers) {
      centerMarkersOnMap({ googleMaps, map, markers });
    }
  }

  _addListingToMap = (listing) => {
    const { map } = this.state;
    const { googleMaps, getMarkerConfig } = this.props;

    const markerConfig = getMarkerConfig({
      map,
      googleMaps,
      listing,
    });

    const marker = new googleMaps.Marker({
      map: map,
      position: {
        lat: listing.locationLatitude,
        lng: listing.locationLongitude,
      },
      ...markerConfig,
    });

    googleMaps.event.addListener(marker, 'click', () => {
      this._onClickMarker(listing);
    });

    return marker;
  }

  _removeListingsFromMap = (listingIds) => {
    const { markers } = this.state;

    listingIds.forEach((listingId) => {
      const marker = markers.get(listingId);
      if (marker) {
        marker.setMap(null);
      }
      markers.delete(listingId);
    });

    this.setState({ markers });
  }

  _removeAllMarkers = () => {
    const { markers } = this.state;
    this._removeListingsFromMap(Array.from(markers.keys()));
  };

  _onClickMarker = (listing) => {
    const { onClickListing } = this.props;

    if (onClickListing) {
      onClickListing(listing);
    }
  }

  render() {
    const {
      getGoogleMapsConfig,
      googleMaps,
      widgets,
    } = this.props;

    return (
      <div className='SrMapPageContainer mapContainer'>
        {widgets}
        <div className='SrMapPageContent innerMapContainer'>
          <GoogleMap
            googleMaps={googleMaps}
            {...getGoogleMapsConfig(googleMaps)}
            onLoaded={this._onLoadedMap}
          />
        </div>
      </div>
    );
  }
}

SrListingsMapViewManager.displayName = 'SrListingsMapViewManager';

export default SrListingsMapViewManager;
